import { EnhancedDeal, ProjectPhase, TimberProject, TransactionSplit } from '../types';
import { getProjectDisplayName } from './projectName';

/** Picker value suffix for the preconstruction phase of a deal. */
export const QB_LINK_PRECON_SUFFIX = '::precon';

export interface QBLinkDealOption {
  /** Unique picker value — plain dealId or `${dealId}::precon`. */
  id: string;
  dealId: string;
  label: string;
  rawTitle: string;
  searchText: string;
  projectPhase?: ProjectPhase;
}

/**
 * Legacy docs (and some agent fixtures) still use `open` for in-progress deals.
 * Typed DealOutcome is won|lost|active — treat `open` like `active` for pickers.
 */
function isActiveLikeOutcome(outcome: string | undefined): boolean {
  return outcome === 'active' || outcome === 'open';
}

/** Search aliases so `#0067`, `0067`, and padded forms all match. */
function projectNumberSearchAliases(projectNumber: string | undefined): string[] {
  const raw = (projectNumber || '').trim();
  if (!raw) return [];
  const noHash = raw.replace(/^#/, '');
  const withHash = noHash ? `#${noHash}` : '';
  return Array.from(new Set([raw, noHash, withHash].filter(Boolean)));
}

function dealSearchText(d: EnhancedDeal, displayName: string): string {
  return [
    ...projectNumberSearchAliases(d.projectNumber),
    d.clientLabel,
    d.descriptor,
    d.title,
    d.contactName,
    displayName,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

export function encodeQBLinkTargetId(dealId: string, phase?: ProjectPhase): string {
  if (phase === 'preconstruction') return `${dealId}${QB_LINK_PRECON_SUFFIX}`;
  return dealId;
}

export function parseQBLinkTargetId(raw: string | null | undefined): {
  dealId: string | null;
  projectPhase?: ProjectPhase;
} {
  if (!raw) return { dealId: null };
  if (raw.endsWith(QB_LINK_PRECON_SUFFIX)) {
    return {
      dealId: raw.slice(0, -QB_LINK_PRECON_SUFFIX.length) || null,
      projectPhase: 'preconstruction',
    };
  }
  return { dealId: raw, projectPhase: 'contracted' };
}

/** Picker value for an existing split row. */
export function splitToLinkTargetId(slice: Pick<TransactionSplit, 'dealId' | 'projectPhase'>): string {
  if (!slice.dealId) return '';
  return encodeQBLinkTargetId(slice.dealId, slice.projectPhase);
}

/**
 * Build project link targets for QB transactions, card expenses, and
 * Purchasing job pickers (DoA / spend classification).
 *
 * Includes:
 * - won deals (always)
 * - active/`open` deals with an open Hub project in estimating, preconstruction,
 *   or contracted (CRM #58 — e.g. LCTF Barn stays active while under contract)
 * - any other non-lost deal that already has a project number (CRM #57 / #0067
 *   when outcome or Hub phase is out of sync)
 *
 * When a deal has both precon and contracted Hub projects open, list them as
 * separate picker rows.
 */
export function buildQBLinkDealOptions(
  deals: EnhancedDeal[],
  timberProjects: TimberProject[],
): QBLinkDealOption[] {
  const openEstimatingByDeal = new Set<string>();
  const openPreconByDeal = new Set<string>();
  const openContractedByDeal = new Set<string>();

  for (const project of timberProjects) {
    if (!project.dealId || project.status === 'completed') continue;
    if (project.phase === 'estimating') openEstimatingByDeal.add(project.dealId);
    else if (project.phase === 'preconstruction') openPreconByDeal.add(project.dealId);
    else openContractedByDeal.add(project.dealId);
  }

  const options: QBLinkDealOption[] = [];
  const wonDeals = deals.filter(d => d.outcome === 'won' && d.id);

  for (const deal of wonDeals) {
    const dealId = deal.id as string;
    const displayName = getProjectDisplayName(deal) || 'Untitled deal';
    const clientSuffix = deal.contactName ? ` · ${deal.contactName}` : '';
    const baseSearch = dealSearchText(deal, displayName);
    const hasPrecon = openPreconByDeal.has(dealId);
    const hasContracted = openContractedByDeal.has(dealId);
    const historicalSuffix = deal.plHistoricalShell ? ' · Completed (P&L)' : '';

    if (hasPrecon && hasContracted) {
      options.push({
        id: encodeQBLinkTargetId(dealId, 'contracted'),
        dealId,
        projectPhase: 'contracted',
        label: `${displayName}${clientSuffix} · Under Contract${historicalSuffix}`,
        rawTitle: displayName,
        searchText: `${baseSearch} under contract contracted${deal.plHistoricalShell ? ' historical pl completed' : ''}`,
      });
      options.push({
        id: encodeQBLinkTargetId(dealId, 'preconstruction'),
        dealId,
        projectPhase: 'preconstruction',
        label: `${displayName}${clientSuffix} · Preconstruction${historicalSuffix}`,
        rawTitle: displayName,
        searchText: `${baseSearch} preconstruction precon${deal.plHistoricalShell ? ' historical pl completed' : ''}`,
      });
    } else {
      options.push({
        id: dealId,
        dealId,
        label: `${displayName}${clientSuffix}${historicalSuffix}`,
        rawTitle: displayName,
        searchText: deal.plHistoricalShell ? `${baseSearch} historical pl completed` : baseSearch,
      });
    }
  }

  const wonIds = new Set(wonDeals.map(d => d.id as string));
  for (const deal of deals) {
    if (!isActiveLikeOutcome(deal.outcome) || !deal.id || wonIds.has(deal.id)) continue;

    const dealId = deal.id;
    const hasPrecon = openPreconByDeal.has(dealId);
    const hasContracted = openContractedByDeal.has(dealId);
    const hasEstimating = openEstimatingByDeal.has(dealId);
    if (!hasPrecon && !hasContracted && !hasEstimating) continue;

    const displayName = getProjectDisplayName(deal) || 'Untitled deal';
    const clientSuffix = deal.contactName ? ` · ${deal.contactName}` : '';
    const baseSearch = dealSearchText(deal, displayName);

    if (hasPrecon && hasContracted) {
      options.push({
        id: encodeQBLinkTargetId(dealId, 'contracted'),
        dealId,
        projectPhase: 'contracted',
        label: `${displayName}${clientSuffix} · Under Contract`,
        rawTitle: displayName,
        searchText: `${baseSearch} under contract contracted`,
      });
      options.push({
        id: encodeQBLinkTargetId(dealId, 'preconstruction'),
        dealId,
        projectPhase: 'preconstruction',
        label: `${displayName}${clientSuffix} · Preconstruction`,
        rawTitle: displayName,
        searchText: `${baseSearch} preconstruction precon`,
      });
      continue;
    }

    if (hasPrecon) {
      options.push({
        id: encodeQBLinkTargetId(dealId, 'preconstruction'),
        dealId,
        projectPhase: 'preconstruction',
        label: `${displayName}${clientSuffix} · Preconstruction`,
        rawTitle: displayName,
        searchText: `${baseSearch} preconstruction precon`,
      });
      continue;
    }

    if (hasContracted) {
      options.push({
        id: dealId,
        dealId,
        projectPhase: 'contracted',
        label: `${displayName}${clientSuffix} · Under Contract`,
        rawTitle: displayName,
        searchText: `${baseSearch} under contract contracted`,
      });
      continue;
    }

    options.push({
      id: dealId,
      dealId,
      projectPhase: 'estimating',
      label: `${displayName}${clientSuffix} · Estimating`,
      rawTitle: displayName,
      searchText: `${baseSearch} estimating`,
    });
  }

  // Numbered non-lost deals remain valid cost objects even when outcome is the
  // legacy `open` value or Hub phase rows are missing (CRM feedback #57 / #0067).
  const listedDealIds = new Set(options.map(o => o.dealId));
  for (const deal of deals) {
    if (!deal.id || listedDealIds.has(deal.id)) continue;
    if (deal.outcome === 'lost') continue;
    if (!(deal.projectNumber || '').trim()) continue;

    const dealId = deal.id;
    const displayName = getProjectDisplayName(deal) || 'Untitled deal';
    const clientSuffix = deal.contactName ? ` · ${deal.contactName}` : '';
    const baseSearch = dealSearchText(deal, displayName);

    if (openPreconByDeal.has(dealId) && openContractedByDeal.has(dealId)) {
      options.push({
        id: encodeQBLinkTargetId(dealId, 'contracted'),
        dealId,
        projectPhase: 'contracted',
        label: `${displayName}${clientSuffix} · Under Contract`,
        rawTitle: displayName,
        searchText: `${baseSearch} under contract contracted`,
      });
      options.push({
        id: encodeQBLinkTargetId(dealId, 'preconstruction'),
        dealId,
        projectPhase: 'preconstruction',
        label: `${displayName}${clientSuffix} · Preconstruction`,
        rawTitle: displayName,
        searchText: `${baseSearch} preconstruction precon`,
      });
    } else if (openPreconByDeal.has(dealId)) {
      options.push({
        id: encodeQBLinkTargetId(dealId, 'preconstruction'),
        dealId,
        projectPhase: 'preconstruction',
        label: `${displayName}${clientSuffix} · Preconstruction`,
        rawTitle: displayName,
        searchText: `${baseSearch} preconstruction precon`,
      });
    } else if (openEstimatingByDeal.has(dealId)) {
      options.push({
        id: dealId,
        dealId,
        projectPhase: 'estimating',
        label: `${displayName}${clientSuffix} · Estimating`,
        rawTitle: displayName,
        searchText: `${baseSearch} estimating`,
      });
    } else if (openContractedByDeal.has(dealId)) {
      options.push({
        id: dealId,
        dealId,
        projectPhase: 'contracted',
        label: `${displayName}${clientSuffix} · Under Contract`,
        rawTitle: displayName,
        searchText: `${baseSearch} under contract contracted`,
      });
    } else {
      options.push({
        id: dealId,
        dealId,
        label: `${displayName}${clientSuffix}`,
        rawTitle: displayName,
        searchText: baseSearch,
      });
    }
    listedDealIds.add(dealId);
  }

  return options.sort((a, b) => a.label.localeCompare(b.label));
}

export type ProjectPLGroup = 'contracted' | 'preconstruction' | 'estimating';

/** Highest open Hub phase for a deal. Contracted wins, then precon, then estimating. */
export function classifyPLDealGroup(
  deal: Pick<EnhancedDeal, 'id' | 'outcome'>,
  timberProjects: TimberProject[],
): ProjectPLGroup {
  let hasEstimating = false;
  let hasPrecon = false;
  let hasContracted = false;
  for (const project of timberProjects) {
    if (!deal.id || project.dealId !== deal.id || project.status === 'completed') continue;
    if (project.phase === 'estimating') hasEstimating = true;
    else if (project.phase === 'preconstruction') hasPrecon = true;
    else hasContracted = true;
  }
  if (hasContracted) return 'contracted';
  if (hasPrecon) return 'preconstruction';
  if (hasEstimating) return 'estimating';
  return deal.outcome === 'won' ? 'contracted' : 'estimating';
}

/**
 * Won deals plus active deals with an open Hub project (estimating,
 * preconstruction, or contracted). Contracted-active covers internal builds
 * that never get marked won but still need P&L / job spend tagging.
 */
export function pickPLDeals(deals: EnhancedDeal[], timberProjects: TimberProject[]): EnhancedDeal[] {
  const openHubDealIds = new Set(
    timberProjects
      .filter(p => p.dealId && p.status !== 'completed')
      .map(p => p.dealId as string),
  );

  const won = deals.filter(d => d.outcome === 'won' && d.id);
  const wonIds = new Set(won.map(d => d.id as string));
  const activeWithHub = deals.filter(
    d => d.outcome === 'active' && d.id && openHubDealIds.has(d.id) && !wonIds.has(d.id),
  );

  return [...won, ...activeWithHub];
}

export function phaseLabel(phase?: ProjectPhase): string {
  if (phase === 'estimating') return 'Estimating';
  if (phase === 'preconstruction') return 'Preconstruction';
  if (phase === 'contracted') return 'Under Contract';
  return '';
}
